import { Injectable } from '@angular/core';
import { combineLatest, filter, finalize, map, shareReplay } from 'rxjs';
import { setContext } from '@apollo/client/link/context';
import { v4 as uuidv4 } from 'uuid'
import { onError } from "@apollo/client/link/error";
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { HttpHeaders } from '@angular/common/http';
import { HttpLink } from "apollo-angular/http";
import { ApolloClient, ApolloLink, InMemoryCache, from } from '@apollo/client';
import { AuthService } from '../auth.service';
import { AbstractionLayerConfig, FenixDomainDataConfig, authentication } from 'src/environments/environment';
import { LaunchdarklyService } from './launchdarkly.service';
import { LoadingService } from '../loading.serivce';

import { v4 as uuid } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class ApolloClientService {

  constructor(
    private httpLink: HttpLink,
    private authService: AuthService,
    private launchdarklyService: LaunchdarklyService,
    private loadingService: LoadingService
  ) { }

  /**
   * Observable of apollo client.
   */
  public apolloClient$ = combineLatest({
    userData: this.authService.userData$,
    launchDarklyFlags: this.launchdarklyService.launchDarklyFlagsWithFallback$,
  }).pipe(
    filter((source) => !!source.userData.isLoggedIn),//
    map(source => {
      // Create http link.
      const useStaging = source.launchDarklyFlags['staging'] !== false;
      const launchDarklyEnvironmentUri = useStaging
        ? AbstractionLayerConfig.stagingUri
        : AbstractionLayerConfig.releaseUri;
      const uriLink = this.httpLink.create({ uri: launchDarklyEnvironmentUri });
      console.log('AbstractionLayerConfig url: ' + launchDarklyEnvironmentUri)

      // Set headers for authentication and correlation.
      const headerLink = setContext((_, { headers }) => {
        const correlationId = uuidv4()
        const requestId = uuidv4()
        const sessionId = source.userData.sessionUID ?? 'anonymous'

        return {
          // apollo-angular wraps a plain object in `new HttpHeaders(...)` at send time,
          // so the runtime value stays a plain object (keeping the correlation-id
          // readbacks below working); the cast only satisfies v4's HttpHeaders typing.
          headers: {
            ...headers,
            'x-correlation-id': correlationId,
            'x-request-id': requestId,
            'authorization': sessionId
          } as unknown as HttpHeaders
        }
      });

      // Configure error handling.
      const errorLink = onError(({ operation, error }) => {
        const correlationId = operation.getContext()['headers']['x-correlation-id'];
        if (CombinedGraphQLErrors.is(error)) {
          error.errors.forEach(({ message, locations, path }) => {
            console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`, correlationId)
          });
        } else if (error) {
          console.log(`[Network error]: ${JSON.stringify(error)}`, correlationId)
          if (error.message.includes('401') || error.message.includes('403')) {
            this.authService.clearSession();
          }
        }
      });

      const loadingLink = new ApolloLink((operation, forward) => {
        const skipGlobalLoading = !!operation.getContext()?.skipGlobalLoading;
        const operationName = operation.operationName ?? '';
        if (!skipGlobalLoading) this.loadingService.show(operationName);

        // Apollo Client v4 links are RxJS-based; finalize() runs on complete,
        // error, and unsubscribe, mirroring the previous manual cleanup.
        return forward(operation).pipe(
          finalize(() => {
            if (!skipGlobalLoading) this.loadingService.hide(operationName);
          })
        );
      });

      // Start and end time for requests, for debug purposes.
      const timeStartLink = new ApolloLink((operation, forward) => {
        const correlationId = operation.getContext()['headers']['x-correlation-id'];
        const startTime = new Date();
        console.log(`[${operation.operationName}] Sending request`, correlationId);
        operation.setContext({ start: startTime });
        return forward(operation);
      });

      const timeEndLink = new ApolloLink((operation, forward) => {
        return forward(operation).pipe(map((data) => {
          const correlationId = operation.getContext()['headers']['x-correlation-id'];
          const duration = new Date().getTime() - operation.getContext()['start'].getTime();
          console.log(`[${operation.operationName}] took ${duration} to complete`, correlationId);
          return data;
        }))
      });

      // Return client to subscribers.
      return new ApolloClient({
        link: from([errorLink, headerLink, loadingLink, timeStartLink, timeEndLink, uriLink]),
        cache: new InMemoryCache(),
        defaultOptions: {
          // Apollo Client v4 gates a default `errorPolicy` behind a `DeclareDefaultOptions`
          // type augmentation; cast to keep the existing 'all' behaviour without it.
          query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all' as any
          },
          watchQuery: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all' as any
          }
        }
      })
    }),
    filter(client => !!client),
    shareReplay(1)
  )

  /**
   * Apollo client for the new Go Lambda API (fenix-domain-data).
   * Uses the same auth pattern but points to the FenixDomainDataConfig endpoints.
   */
  public fenixDomainDataClient$ = combineLatest({
    userData: this.authService.userData$,
    launchDarklyFlags: this.launchdarklyService.launchDarklyFlags$
  }).pipe(
    filter((source) => !!source.userData.isLoggedIn),
    map(source => {
      if (!FenixDomainDataConfig) return null;

      const uri = source.launchDarklyFlags['staging']
        ? FenixDomainDataConfig.stagingUri
        : FenixDomainDataConfig.releaseUri;
      const uriLink = this.httpLink.create({ uri });

      const headerLink = setContext((_, { headers }) => ({
        headers: {
          ...headers,
          'x-correlation-id': uuidv4(),
          'x-request-id': uuidv4(),
          'authorization': source.userData.sessionUID ?? 'anonymous'
        } as unknown as HttpHeaders
      }));

      const errorLink = onError(({ error }) => {
        if (CombinedGraphQLErrors.is(error)) {
          error.errors.forEach(({ message, locations, path }) => {
            console.log(`[FenixDomainData GraphQL error]: Message: ${message}, Path: ${path}`);
          });
        } else if (error) {
          console.log(`[FenixDomainData Network error]: ${JSON.stringify(error)}`);
        }
      });

      return new ApolloClient({
        link: from([errorLink, headerLink, uriLink]),
        cache: new InMemoryCache(),
        defaultOptions: {
          query: { fetchPolicy: 'no-cache', errorPolicy: 'all' as any },
          watchQuery: { fetchPolicy: 'no-cache', errorPolicy: 'all' as any }
        }
      });
    }),
    filter(client => !!client),
    shareReplay(1)
  );

  /**
   * Observable of data which is common between all available clients.
   */
  private commonClientConfiguration$ = this.authService.userData$.pipe(
    map(userData => {
      // Set headers for authentication and correlation.
      const headerLink = setContext((_, { headers }) => {
        // this.loggerService.logDebug('setContext');
        const correlationId = uuid();
        const requestId = uuid();
        const sessionId = userData.sessionUID || 'anonymous';
        // this.loggerService.logDebug('graphql sessionId: ' + sessionId);

        return {
          headers: {
            ...headers,
            'x-correlation-id': correlationId,
            'x-request-id': requestId,
            authorization: sessionId,
          } as unknown as HttpHeaders,
        };
      });

      // Configure error handling.
      const errorLink = onError(({ operation, error }) => {
        // this.loggerService.logDebug('onError');
        const correlationId = operation.getContext()['headers']['x-correlation-id'];
        if (CombinedGraphQLErrors.is(error)) {
          error.errors.forEach(({ message, locations, path }) => {
            // this.loggerService.logError(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`, correlationId);
          });
        } else if (error) {
          if (error.message.includes('401') || error.message.includes('403')) {
            this.authService.clearSession();
          }
          // this.loggerService.logError(`[Network error]: ${JSON.stringify(error)}`, correlationId);
        }
      });

      const timeStartLink = new ApolloLink((operation, forward) => {
        // this.loggerService.logDebug('forward start');
        const correlationId = operation.getContext()['headers']['x-correlation-id'];
        const startTime = new Date();
        // this.loggerService.logInformation(`[${operation.operationName}] Sending request`, correlationId);
        operation.setContext({ start: startTime });
        // this.loggerService.logDebug('forward end');
        return forward(operation);
      });

      const timeEndLink = new ApolloLink((operation, forward) => {
        // this.loggerService.logDebug('ApolloLink');
        return forward(operation).pipe(map(data => {
          const correlationId = operation.getContext()['headers']['x-correlation-id'];
          const duration = new Date().getTime() - operation.getContext()['start'].getTime();
          // this.loggerService.logInformation(`[${operation.operationName}] took ${duration} to complete`, correlationId);
          return data;
        }));
      });

      return {
        headerLink: headerLink,
        errorLink: errorLink,
        timeStartLink: timeStartLink,
        timeEndLink: timeEndLink,
      };
    }),
    shareReplay(1),
  );

  /**
   * Observable of apollo client for communicating with abstraction layer.
   */
  public authLayerClient = combineLatest({
    commonConfiguration: this.commonClientConfiguration$,
    launchDarklyFlags: this.launchdarklyService.launchDarklyFlagsWithFallback$,
  }).pipe(
    map(source => {
      console.log('combineLatest source:', source);
      // Set uri based on environment from LaunchDarkly flags.
      const useStaging = source.launchDarklyFlags['staging'] !== false;
      const authLayerUri = useStaging
        ? authentication.authLayerStagingUrl
        : authentication.authLayerReleaseUrl;
      const uriLink = this.httpLink.create({ uri: authLayerUri });

      // Return client to subscribers.
      return new ApolloClient({
        link: from([source.commonConfiguration.errorLink, source.commonConfiguration.headerLink, source.commonConfiguration.timeStartLink, source.commonConfiguration.timeEndLink, uriLink]),
        cache: new InMemoryCache(),
      });
    }),
    filter(client => !!client),
    shareReplay(1),
  );
}
